import { IAIImageProject, IReferenceImage } from 'store/types/aiImage'
import {
  IBaseUploadedMediaItem,
  IMediaItem,
  IMyFilesMediaItem
} from 'types/media'
import { MediaStatus } from 'types/media'
import { resolveCompletedMediaRect } from './media'

export const convertMediaFilesToReferenceImages = (
  medias: IBaseUploadedMediaItem[]
): IReferenceImage[] =>
  medias.map(media => ({
    id: media.id,
    url: media.filePath,
    thumbnailUrl: media.filePath,
    width: 100,
    height: 100
  }))

/**
 * Updates a media item with completed AI image data.
 * Preserves the item's current width/height/coordinates verbatim when the new
 * asset has the same aspect ratio as the previous one, so the user's manual
 * resize/reposition (before or during regeneration) survives completion. If
 * the user picked a different aspect ratio for the regeneration, falls back
 * to a fresh FIT-to-canvas rect so the new asset isn't distorted by the old
 * bounding box.
 */
export const updateMediaItemWithAIImageData = (
  mediaItem: IMediaItem,
  filePath: string,
  originalWidth: number,
  originalHeight: number,
  canvasDimensions: { width: number; height: number }
) => {
  const { width, height, x, y } = resolveCompletedMediaRect({
    currentWidth: mediaItem.width,
    currentHeight: mediaItem.height,
    currentCoordinates: mediaItem.coordinates,
    newOriginal: { width: originalWidth, height: originalHeight },
    canvasDimensions,
    rotation: mediaItem.rotation
  })

  return {
    id: mediaItem.id,
    updates: {
      assetId: mediaItem.assetId,
      width,
      height,
      coordinates: { x, y },
      meta: {
        ...mediaItem.meta,
        path: filePath,
        originalWidth,
        originalHeight,
        status: MediaStatus.OK
      }
    }
  }
}

export const convertIAIImageProjectToMyFilesMediaItem = (
  aiImageProject: IAIImageProject
): IMyFilesMediaItem => {
  const image = aiImageProject.images[0] // Get the first generated image

  return {
    id: aiImageProject.id,
    folderId: 0, // Default value since AI images don't have folders
    createdAt: new Date().toISOString(), // Current timestamp as fallback
    updatedAt: new Date().toISOString(), // Current timestamp as fallback
    fileName: `ai-image-${aiImageProject.id}`,
    filePath: image.filePath,
    downloadPath: image.filePath, // Same as filePath for AI images
    fileSize: 0, // AI images don't have file size info in the project
    mime: 'image/jpeg', // Default mime type for AI images
    width: aiImageProject.width,
    height: aiImageProject.height,
    thumbnailPath: image.thumbnailPath
  }
}

export const filterValidImages = async (
  images: string[]
): Promise<string[]> => {
  const results = await Promise.all(
    images.map(async url => {
      try {
        const res = await fetch(url, { method: 'HEAD', cache: 'no-store' })
        return res.ok ? url : null
      } catch {
        return null
      }
    })
  )

  return results.filter((url): url is string => Boolean(url))
}
